import React, { useEffect } from 'react';
import SEOHead from './SEOHead';

export const APP_STORE_URL = 'https://apps.apple.com/us/app/yunlen/id6751148625';
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.yunlen.app';

/**
 * Last resort for an app link the OS did not hand to the app — the app is not
 * installed, or the reader is on desktop.
 *
 * The custom scheme keeps the path the reader actually asked for, so a link to
 * one transaction still opens that transaction on a device where the app is
 * installed but universal links did not fire.
 */
const AppRedirect = () => {
    useEffect(() => {
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

        // Desktop has no app to open: go straight to the download page rather
        // than sitting on a spinner for two seconds.
        if (!isMobile) {
            window.location.replace('/download');
            return;
        }

        // `yunlen:/` + `/activity/T1` gives `yunlen://activity/T1`, whose first
        // segment is the destination the app's link table reads. Nesting it
        // under `app/` instead would parse as plain "open the app" and lose the
        // destination.
        const { pathname, search } = window.location;
        const path = pathname === '/app' || pathname === '/' ? '' : pathname;
        window.location.href = path
            ? `yunlen:/${path}${search}`
            : `yunlen://app${search}`;

        // If the scheme opened the app this page is backgrounded and the timer
        // never fires; if it did not, the reader needs the store.
        const timer = setTimeout(() => {
            window.location.href = isIOS ? APP_STORE_URL : PLAY_STORE_URL;
        }, 2000);

        const cancel = () => document.hidden && clearTimeout(timer);
        document.addEventListener('visibilitychange', cancel);
        return () => {
            clearTimeout(timer);
            document.removeEventListener('visibilitychange', cancel);
        };
    }, []);

    return (
        <>
            <SEOHead
                title="Open Yunlen App - Yunlen"
                description="Redirecting you to the Yunlen mobile app. Download Yunlen for secure financial services, virtual cards, and global transfers."
                canonicalUrl="https://yunlen.app/app"
                noIndex={true}
            />
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
                    <div className="mb-6">
                        <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Opening Yunlen App</h1>
                        <p className="text-gray-600">
                            Redirecting you to the Yunlen mobile app...
                        </p>
                    </div>

                    <div className="space-y-4">
                        <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                            <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                            <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                            <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                        </div>

                        <p className="text-xs text-gray-400">
                            If the app doesn't open automatically, you can download it from the app store.
                        </p>

                        <div className="flex space-x-4 justify-center mt-6">
                            <a
                                href={APP_STORE_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                            >
                                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.24-3.05-2.45-1.64-2.8-1.64-5.5 0-8.3.82-1.4 2.3-2.3 3.9-2.3 1.53 0 2.8.8 3.6.8.8 0 2.2-1 3.7-.85.64.03 2.44.26 3.6 1.85-.09.06-2.17 1.27-2.15 3.8.03 3.01 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                                </svg>
                                App Store
                            </a>

                            <a
                                href={PLAY_STORE_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            >
                                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                                </svg>
                                Google Play
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AppRedirect;
